/**
 * LogTimeDialog - Manual (explicit) time entry for a lead
 */

import React, { useState } from 'react';
import { useAuth } from '../../utils/AuthContext';
import { saveLeadTimeEntry } from '../../utils/leadTimeTrackingService';
import { X } from 'lucide-react';
import './LogTimeDialog.css';

interface LogTimeDialogProps {
  leadId: string;
  dealId?: string;
  proposalId?: string;
  onClose: () => void;
  onSaved?: () => void;
}

export default function LogTimeDialog({ leadId, dealId, proposalId, onClose, onSaved }: LogTimeDialogProps) {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [minutes, setMinutes] = useState(30);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || minutes <= 0) return;

    setSaving(true);
    setError(null);

    try {
      const startTime = new Date(date + 'T00:00:00');
      const endTime = new Date(startTime.getTime() + minutes * 60 * 1000);

      await saveLeadTimeEntry({
        leadId,
        userId: user.id,
        userName: user.name || 'Unknown',
        type: 'explicit',
        minutes,
        startTime,
        endTime,
        note: note.trim() || undefined,
        dealId,
        proposalId,
        createdAt: new Date(),
      });

      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save time entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="log-time-dialog-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="log-time-title">
      <div className="log-time-dialog" onClick={e => e.stopPropagation()}>
        <div className="log-time-dialog-header">
          <h2 id="log-time-title">Log Time</h2>
          <button type="button" className="log-time-dialog-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="log-time-dialog-form">
          <div className="log-time-dialog-field">
            <label htmlFor="log-time-date">Date</label>
            <input
              id="log-time-date"
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
          </div>
          <div className="log-time-dialog-field">
            <label htmlFor="log-time-minutes">Minutes</label>
            <input
              id="log-time-minutes"
              type="number"
              min={1}
              max={480}
              value={minutes}
              onChange={e => setMinutes(Math.max(0, parseInt(e.target.value, 10) || 0))}
            />
          </div>

          <div className="log-time-dialog-field">
            <label htmlFor="log-time-note">Note (optional)</label>
            <textarea
              id="log-time-note"
              rows={3}
              placeholder="e.g. Client call, site visit, proposal review"
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>

          {error && <div className="log-time-dialog-error">{error}</div>}

          <div className="log-time-dialog-actions">
            <button type="button" className="log-time-dialog-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="log-time-dialog-submit" disabled={saving || minutes <= 0}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
